
import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { toast } from "@/hooks/use-toast";
import * as logger from "@/utils/logger";
import { ENABLE_FREE_ACCESS } from "@/utils/freeAccessConfig";

interface TimedAccessContextType {
  hasFreeAccess: boolean;
  timeRemaining: number;
  startFreeAccess: (redirectUrl?: string) => void;
  endFreeAccess: () => void;
}

const FREE_ACCESS_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds
const FREE_ACCESS_KEY = "helloml_free_access_start";
const FREE_ACCESS_USED_KEY = "helloml_free_access_used";

const TimedAccessContext = createContext<TimedAccessContextType>({
  hasFreeAccess: false,
  timeRemaining: 0,
  startFreeAccess: () => {},
  endFreeAccess: () => {},
});

export const TimedAccessProvider = ({ children }: { children: React.ReactNode }) => {
  const [hasFreeAccess, setHasFreeAccess] = useState<boolean>(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [redirectAfterLogin, setRedirectAfterLogin] = useState<string | null>(null);
  const { user } = useAuth();

  logger.log("TimedAccessProvider - Initializing with localStorage state:", {
    freeAccessKey: localStorage.getItem(FREE_ACCESS_KEY),
    freeAccessUsedKey: localStorage.getItem(FREE_ACCESS_USED_KEY),
    timestamp: new Date().toISOString()
  });

  // Check if free access is active
  useEffect(() => {
    if (!ENABLE_FREE_ACCESS) {
      setHasFreeAccess(false);
      setTimeRemaining(0);
      localStorage.removeItem(FREE_ACCESS_KEY);
      return;
    }
    const checkFreeAccess = () => {
      try {
        const startTime = localStorage.getItem(FREE_ACCESS_KEY);
        const hasUsedFreeAccess = localStorage.getItem(FREE_ACCESS_USED_KEY) === "true";
        
        logger.log("TimedAccessProvider - Checking free access:", {
          startTime,
          hasUsedFreeAccess,
          timestamp: new Date().toISOString()
        });
        
        if (!startTime || hasUsedFreeAccess) {
          setHasFreeAccess(false);
          setTimeRemaining(0);
          return;
        }

        const start = parseInt(startTime, 10);
        const now = Date.now();
        const elapsed = now - start;
        const remaining = FREE_ACCESS_DURATION - elapsed;

        logger.log("TimedAccessProvider - Calculating time remaining:", {
          start,
          now,
          elapsed,
          remaining,
          timestamp: new Date().toISOString()
        });

        if (remaining <= 0) {
          // Free access has expired
          logger.log("TimedAccessProvider - Free access expired", {
            timestamp: new Date().toISOString()
          });
          setHasFreeAccess(false);
          setTimeRemaining(0);
          localStorage.removeItem(FREE_ACCESS_KEY);
          localStorage.setItem(FREE_ACCESS_USED_KEY, "true");
          toast({
            title: "Free access expired",
            description: "Your 10-minute free access has ended. Subscribe to continue.",
          });
        } else {
          // Free access is still active
          logger.log("TimedAccessProvider - Free access is active", {
            remaining,
            timestamp: new Date().toISOString()
          });
          setHasFreeAccess(true);
          setTimeRemaining(remaining);
        }
      } catch (error) {
        logger.error("TimedAccessProvider - Error checking free access:", error);
        setHasFreeAccess(false);
        setTimeRemaining(0);
      }
    };

    // Initial check
    checkFreeAccess();

    // Set up timer to update remaining time
    const interval = setInterval(() => {
      checkFreeAccess();
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const startFreeAccess = (redirectUrl?: string) => {
    if (!ENABLE_FREE_ACCESS) {
      toast({
        title: "Free access disabled",
        description: "Free access is not available at this time.",
        variant: "destructive",
      });
      return;
    }
    logger.log("TimedAccessProvider - Starting free access", {
      user: user ? `User ID: ${user.id}` : "No user",
      redirectUrl,
      timestamp: new Date().toISOString()
    });
    
    if (!user) {
      // Store redirect URL for after login
      logger.log("TimedAccessProvider - User not logged in, storing redirect URL", {
        redirectUrl: redirectUrl || '/get-premium',
        timestamp: new Date().toISOString()
      });
      
      setRedirectAfterLogin(redirectUrl || '/get-premium');
      
      // Use window.location to navigate to login
      toast({
        title: "Login required",
        description: "Please login to start your free access period.",
      });
      
      const returnUrl = redirectUrl ? encodeURIComponent(redirectUrl) : 'get-premium';
      window.location.href = `/login?returnTo=${returnUrl}&freeAccess=true`;
      return;
    }

    // Check if user has already used free access
    const hasUsedFreeAccess = localStorage.getItem(FREE_ACCESS_USED_KEY) === "true";
    if (hasUsedFreeAccess) {
      logger.log("TimedAccessProvider - Free access already used", {
        timestamp: new Date().toISOString()
      });
      toast({
        title: "Free access already used",
        description: "You've already used your free access period.",
        variant: "destructive",
      });
      return;
    }

    // Start free access
    const now = Date.now();
    logger.log("TimedAccessProvider - Setting free access start time", {
      time: now,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem(FREE_ACCESS_KEY, now.toString());
    setHasFreeAccess(true);
    setTimeRemaining(FREE_ACCESS_DURATION);
    
    toast({
      title: "Free access started!",
      description: "You now have 10 minutes of free access to premium content.",
    });
  };

  const endFreeAccess = () => {
    logger.log("TimedAccessProvider - Ending free access", {
      timestamp: new Date().toISOString()
    });
    localStorage.removeItem(FREE_ACCESS_KEY);
    localStorage.setItem(FREE_ACCESS_USED_KEY, "true");
    setHasFreeAccess(false);
    setTimeRemaining(0);
  };

  return (
    <TimedAccessContext.Provider value={{ hasFreeAccess, timeRemaining, startFreeAccess, endFreeAccess }}>
      {children}
    </TimedAccessContext.Provider>
  );
};

export const useTimedAccess = () => useContext(TimedAccessContext);
